import { AppText } from "@/components/AppText";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { setGlobalSessionId } from "@/services";
import AuthServices from "@/services/authServices";
import { storageUtils } from "@/utils/storage";
import { showError } from "@/utils/toast";
import { Redirect, router } from "expo-router";
import React, { useEffect, useState } from "react";
import {
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import FBTrackingService from "@/services/fbTrackingService";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import useUserStore from "@/store/user";

export default function SignInScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { getUserProfile } = useUserStore();

  useEffect(() => {
    FBTrackingService.trackViewContent("signin");
  }, []);

  const handleSubmit = async () => {
    if (!email || !password) {
      showError("Please enter both email and password", "Validation Error");
      return;
    }

    setIsLoading(true);

    try {
      const response = await AuthServices.signIn({ email, password });

      if (response.success) {
        // Store sessionId in storage
        const sessionId = response.data.sessionId;
        storageUtils.setSessionId(sessionId);

        // Set global session ID for API requests
        setGlobalSessionId(sessionId);

        // Get user profile and identify user
        const userProfile = await getUserProfile();

        // Identify user in analytics platforms
        if (userProfile?.id) {
          FBTrackingService.setUserId(userProfile.id);
        }

        // Track sign in
        FBTrackingService.trackEvent(TRACKING_EVENTS.USER_SIGNED_IN, {
          eventData: {
            email,
            user_id: userProfile?.id,
          },
        });

        // Navigate to tabs
        router.replace("/(tabs)/feeds");
      } else {
        // Handle API error
        showError(response.message || "Sign in failed", "Sign In Failed");
      }
    } catch (error) {
      showError("An unexpected error occurred", "Error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    router.push("/forgot-password");
  };

  const handleCreateAccount = () => {
    router.push("/signup?startAt=15");
  };

  if (Platform.OS === "web") {
    return <Redirect href="/" />;
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Back Button */}
      <View style={styles.backButton}>
        <TouchableOpacity onPress={() => router.back()}>
          <Ionicons name="chevron-back" size={24} color="#000" />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Sign In Form */}
          <View style={styles.form}>
            <AppText style={styles.title}>Sign In</AppText>

            <Input
              placeholder="Email"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              editable={!isLoading}
              style={styles.input}
            />

            <Input
              placeholder="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              autoComplete="password"
              editable={!isLoading}
              style={styles.input}
            />

            <Button
              title={isLoading ? "Signing In..." : "Login"}
              onPress={handleSubmit}
              style={[styles.loginButton, isLoading && styles.disabledButton]}
              disabled={isLoading}
            />

            <TouchableOpacity
              onPress={handleForgotPassword}
              style={styles.forgotPasswordContainer}
              disabled={isLoading}
            >
              <AppText style={styles.forgotPassword}>Forgot password?</AppText>
            </TouchableOpacity>
          </View>
        </ScrollView>

        {/* Create Account Button */}
        <TouchableOpacity
          onPress={handleCreateAccount}
          style={styles.createAccountContainer}
          disabled={isLoading}
        >
          <AppText style={styles.createAccountText}>Create an account</AppText>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  backButton: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 16,
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  scrollContent: {
    flexGrow: 1,
    paddingTop: 16,
  },
  form: {},
  title: {
    fontSize: 32,
    fontWeight: "600",
    lineHeight: 40,
    color: "#000",
    marginBottom: 32,
  },
  input: {},
  loginButton: {
    marginTop: 8,
    marginBottom: 24,
    backgroundColor: "#0F9129",
    borderRadius: 8,
    height: 56,
  },
  disabledButton: {
    opacity: 0.6,
  },
  forgotPasswordContainer: {
    alignSelf: "center",
  },
  forgotPassword: {
    fontSize: 14,
    color: "#666",
  },
  createAccountContainer: {
    alignSelf: "center",
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: "center",
    backgroundColor: "#F5F5F5",
    borderRadius: 100,
    marginBottom: 20,
  },
  createAccountText: {
    fontSize: 16,
    color: "#000",
    fontWeight: "500",
  },
});
